import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Circle,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import { MaterialIcon } from "../../../components/shared/MaterialIcon";
import {
  IRegistryDataUpdateItem,
  IRegistryDataUpdateItemResult,
} from "../../../models/RegistryData";
import { useUpdateRegistryData } from "../../../services/api/data/queries/useUpdateRegistryData";

interface IProps {
  registryId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  edits: IRegistryDataUpdateItem[];
}

export const EditsReviewModal = ({
  registryId,
  onClose,
  onSuccess,
  isOpen,
  edits,
}: IProps) => {
  const [results, setResults] = React.useState<IRegistryDataUpdateItemResult[]>(
    []
  );
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>("");

  const toast = useToast();

  const {
    mutateAsync: mutateAsyncUpdate,
    isLoading: isLoadingUpdate,
    isError: isErrorUpdate,
  } = useUpdateRegistryData();

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSuccess = () => {
    setIsSubmitted(false);
    onSuccess();
  };

  const handleOnSubmit = async () => {
    setIsSubmitted(true);
    let resultsUpdate: IRegistryDataUpdateItemResult[] = [];

    try {
      resultsUpdate = await mutateAsyncUpdate({ registryId, updates: edits });
      toast({
        title: "Edits submitted",
        description: "View status of each transaction.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      setResults(resultsUpdate);
    } catch (e: any) {
      let errorMessageUpdate = "Failed to update registry";
      try {
        const statusCode = e.response.status;

        if (statusCode === 501) {
          errorMessageUpdate = "Registry edits not implemented yet.";
          toast({
            title: "Registry not editable",
            description: "We have not supported editing this registry yet.",
            status: "info",
            duration: 2000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Update failed",
            description: "This edit failed to submit. Please contact us.",
            status: "error",
            duration: 2000,
            isClosable: true,
          });
        }
      } catch (e: any) {}

      setErrorMessage(errorMessageUpdate);
    }
  };

  React.useEffect(() => {
    let resultsUpdate: IRegistryDataUpdateItemResult[] = edits.map((edit) => {
      return {
        ...edit,
        isSuccess: false,
      };
    });

    setResults(resultsUpdate);
  }, [edits]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSubmitted ? handleOnSuccess : handleOnClose}
      closeOnOverlayClick={true}
      size="5xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Are these changes correct?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={8}>
            {isErrorUpdate && (
              <Alert status="error" variant={"solid"}>
                <AlertIcon />
                <Text>{errorMessage}</Text>
              </Alert>
            )}
            <Box>
              <Grid templateColumns="min-content 1fr min-content min-content 1fr 1fr">
                <GridItem py={4} px={2}>
                  <Text textStyle={"label"} whiteSpace="nowrap">
                    Record Id
                  </Text>
                </GridItem>
                <GridItem py={4} px={2}>
                  <Text textStyle={"label"}>Field</Text>
                </GridItem>
                <GridItem py={4} px={2}>
                  <Text textStyle={"label"} whiteSpace="nowrap">
                    Required
                  </Text>
                </GridItem>
                <GridItem py={4} px={2}>
                  <Text textStyle={"label"} whiteSpace="nowrap">
                    PHI
                  </Text>
                </GridItem>
                <GridItem py={4} px={2}>
                  <Text textStyle={"label"}>Old Value</Text>
                </GridItem>
                <GridItem py={4} px={2}>
                  <Text textStyle={"label"}>New Value</Text>
                </GridItem>

                {results.map((edit, idx) => {
                  return (
                    <React.Fragment key={idx}>
                      <GridItem
                        borderTopWidth="1px"
                        py={2}
                        px={2}
                        display="flex"
                        alignItems="center"
                      >
                        <Text>{edit.record_id}</Text>
                      </GridItem>

                      <GridItem
                        borderTopWidth="1px"
                        py={2}
                        px={2}
                        display="flex"
                        alignItems="center"
                      >
                        <HStack alignItems="center" flex="1">
                          {edit.fieldversion_mapping && (
                            <Popover placement="right">
                              <PopoverTrigger>
                                <IconButton
                                  aria-label="Show mapping information"
                                  size="xs"
                                  variant="outline"
                                  icon={
                                    <MaterialIcon
                                      name="transform"
                                      variant="outline"
                                      fontSize="1rem"
                                    />
                                  }
                                ></IconButton>
                              </PopoverTrigger>
                              <PopoverContent>
                                <PopoverArrow />
                                {/* <PopoverCloseButton /> */}
                                <PopoverHeader>
                                  <Text textStyle={"label"}>
                                    Field will be mapped to registry field
                                  </Text>
                                </PopoverHeader>
                                <PopoverBody>
                                  {/* <Text>{edit.fieldversion_id}</Text> */}
                                  <Text>{edit.fieldversion_mapping}</Text>
                                </PopoverBody>
                              </PopoverContent>
                            </Popover>
                          )}
                          <Text>{edit.field}</Text>
                        </HStack>
                      </GridItem>
                      <GridItem
                        borderTopWidth="1px"
                        py={2}
                        px={2}
                        display="flex"
                        alignItems="center"
                      >
                        <Box>
                          <Circle>
                            {edit.isRequired && (
                              <MaterialIcon
                                color="success"
                                name="done"
                                variant="outline"
                              />
                            )}
                            {!edit.isRequired && (
                              <MaterialIcon
                                color="danger"
                                name="close"
                                variant="outline"
                                fontSize={"1.5rem"}
                              />
                            )}
                          </Circle>
                        </Box>
                      </GridItem>
                      <GridItem
                        borderTopWidth="1px"
                        py={2}
                        px={2}
                        display="flex"
                        alignItems="center"
                      >
                        <Box>
                          <Circle>
                            {edit.isPhi && (
                              <MaterialIcon
                                color="success"
                                name="done"
                                variant="outline"
                              />
                            )}
                            {!edit.isPhi && (
                              <MaterialIcon
                                color="danger"
                                name="close"
                                variant="outline"
                                fontSize={"1.5rem"}
                              />
                            )}
                          </Circle>
                        </Box>
                      </GridItem>
                      <GridItem
                        borderTopWidth="1px"
                        py={2}
                        px={2}
                        display="flex"
                        alignItems="center"
                      >
                        <Text fontWeight={"light"}>{edit.oldValueDisplay}</Text>
                      </GridItem>
                      <GridItem
                        borderTopWidth="1px"
                        py={2}
                        px={2}
                        display="flex"
                        alignItems="center"
                      >
                        <Text fontWeight={"semibold"}>
                          {edit.newValueDisplay}
                        </Text>
                      </GridItem>

                      {isSubmitted && !isLoadingUpdate && (
                        <GridItem colSpan={6} mb={4}>
                          {edit.isSuccess && (
                            <Alert status="success" variant={"solid"}>
                              <AlertIcon />
                              <Text>Edit is complete</Text>
                            </Alert>
                          )}
                          {edit.isError && (
                            <Alert status="error" variant={"solid"}>
                              <AlertIcon />
                              <Text>Error: {edit.errorMessage}</Text>
                            </Alert>
                          )}
                        </GridItem>
                      )}
                    </React.Fragment>
                  );
                })}
              </Grid>
            </Box>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup isDisabled={isLoadingUpdate}>
            {(!isSubmitted || isLoadingUpdate) && (
              <>
                <Button colorScheme={"red"} onClick={handleOnClose}>
                  No, go back
                </Button>

                <Button
                  colorScheme={"green"}
                  onClick={handleOnSubmit}
                  isLoading={isLoadingUpdate}
                >
                  Yes, this data is correct
                </Button>
              </>
            )}
            {isSubmitted && !isLoadingUpdate && (
              <Button onClick={handleOnSuccess}>Close</Button>
            )}
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
